import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useApp } from "./index";
import { getDexWallet, getStakeEstimateGas } from "../services/app";

const GasFeeContext = createContext({});

// TTL in milliseconds (5 minutes)
const GAS_FEE_TTL = 5 * 60 * 1000;

export const GasFeeProvider = ({ children }) => {
  const { user } = useApp();
  const [gasFee, setGasFee] = useState(null);
  const [nativeBalance, setNativeBalance] = useState(null);
  const [sgtBalance, setSgtBalance] = useState(null);
  const [stakePlans, setStakePlans] = useState(null);
  const [energyConfig, setEnergyConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  

  const isDataStale = useCallback(() => {
    if (!lastFetched) return true;
    const now = Date.now();
    return now - lastFetched > GAS_FEE_TTL;
  }, [lastFetched]);

  const fetchGasFee = useCallback(async (force = false) => {
    if (!user?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const result = await getDexWallet();
      if (result.status === 200) {
        const address = result?.data?.data?.public_address;
        if (address) {
          const res = await getStakeEstimateGas({
            network: "polygon",
            walletAddress: address,
          });
          
          const { gasFee: gasFeeData, nativeBalance: nativeBalanceData, sgtBalance: sgtBalanceData, stakePlans: stakePlansData, energyConfig: energyConfigData } = res?.data || {};
          const timestamp = Date.now();

          setGasFee(gasFeeData);
          setNativeBalance(nativeBalanceData);
          setSgtBalance(sgtBalanceData);
          setStakePlans(stakePlansData);
          setLastFetched(timestamp);
          setEnergyConfig(energyConfigData);
        } else {
          throw new Error("Wallet address not found");
        }
      } else {
        throw new Error("Failed to fetch wallet address");
      }
    } catch (err) {
      console.error("Error loading gas data:", err);
      setError(err);
      setGasFee({});
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  // Fetch on mount if data is stale or not loaded yet
  useEffect(() => {
    if (isDataStale() || !gasFee) {
      fetchGasFee();
    }
  }, [isDataStale, fetchGasFee, gasFee]);

  // Check periodically if data is stale and refetch
  useEffect(() => {
    const interval = setInterval(() => {
      if (isDataStale()) {
        fetchGasFee(true);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [isDataStale, fetchGasFee]);

  const value = {
    gasFee,
    nativeBalance,
    sgtBalance,
    stakePlans,
    energyConfig,
    loading,
    error,
    lastFetched,
    refetch: () => fetchGasFee(true),
    isStale: isDataStale(),
  };

  return (
    <GasFeeContext.Provider value={value}>
      {children}
    </GasFeeContext.Provider>
  );
};

/**
 * 
 * @returns {Object} Object containing gasFee, loading, error, refetch, isStale, nativeBalance, sgtBalance, stakePlans
 * @property {Object} gasFee - Object containing gas fee data
 * @property {number} nativeBalance - Native balance in POL
 * @property {number} sgtBalance - SGT balance in SGT
 * @property {Object} stakePlans - Object containing stake plans data
 * @property {boolean} loading - Boolean indicating if data is loading
 * @property {Object} error - Object containing error data
 * @property {number} lastFetched - Timestamp of last fetched data
 * @property {Function} refetch - Function to refetch data
 * @property {Function} isStale - Function to check if data is stale
 * @example
 * const { gasFee, loading, error, refetch, isStale, nativeBalance, sgtBalance, stakePlans } = useGasFee();
 */
export const useGasFee = () => {
  const context = useContext(GasFeeContext);
  if (!context) {
    throw new Error("useGasFee must be used within a GasFeeProvider");
  }
  return context;
};
